import { useEffect, useRef, useState } from "react";
import type { Position } from "./geo";
import { chooseSnap, haversine, parseMaxspeed, type Snap, type Way } from "./snap";
import { fetchRoadData } from "./roadClient";

export type SpeedLimit = {
  kmh: number | null;
  raw?: string;
  highway?: string | null;
  name?: string | null;
};

export type RoadContext = {
  /** position after snap-to-road, or raw if no road is close enough */
  position: Position | null;
  /** raw GPS as reported, for debugging */
  raw: Position | null;
  /** the way we're currently on, if any */
  way: Way | null;
  /** posted speed limit (km/h) for the current way, or null */
  limit: SpeedLimit;
  /** last API error, if any */
  error: string | null;
};

const FETCH_RADIUS = 300; // metres
const REFETCH_AT = 180;   // metres from buffer centre

/**
 * useRoadContext — map-matches a raw GPS position to the nearest road and
 * caches a buffer of nearby road geometry so we don't hammer Overpass.
 *
 * Why: bare GPS jitters off the actual road, which makes the marker
 * dance and per-point Overpass calls return inconsistent speed limits.
 * Snapping to the closest way (with heading bias + hysteresis) gives a
 * stable position and lets us read the speed limit straight off the
 * snapped way's tags.
 */
export function useRoadContext(raw: Position | null): RoadContext {
  const [ways, setWays] = useState<Way[]>([]);
  const [center, setCenter] = useState<[number, number] | null>(null);
  const [snapped, setSnapped] = useState<Snap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stickyWayIdRef = useRef<number | null>(null);
  const inflightRef = useRef<AbortController | null>(null);
  const lastFetchCenterRef = useRef<[number, number] | null>(null);

  // Coarse position grid (~110 m, 3 decimal places). The fetch effect
  // re-runs only when this changes — so 60 fps position updates don't
  // re-fire fetches, but the FIRST non-null position immediately does.
  // The haversine check inside still gates real refetches by REFETCH_AT.
  const latKey = raw ? raw.lat.toFixed(3) : null;
  const lonKey = raw ? raw.lon.toFixed(3) : null;

  useEffect(() => {
    if (latKey == null || lonKey == null) return;
    const lat = Number(latKey);
    const lon = Number(lonKey);
    const c = lastFetchCenterRef.current;
    if (c) {
      const d = haversine(lat, lon, c[0], c[1]);
      if (d < REFETCH_AT) return; // buffer still covers us
    }
    if (inflightRef.current) return; // already fetching
    const ctrl = new AbortController();
    inflightRef.current = ctrl;
    lastFetchCenterRef.current = [lat, lon];
    // Fetch + JSON.parse happen on a Web Worker (see roadClient) so the
    // large Overpass payload never parses on the main thread.
    fetchRoadData<{ center: [number, number]; ways: Way[] }>(
      `/api/nearby-roads?lat=${lat}&lon=${lon}&radius=${FETCH_RADIUS}`,
      ctrl.signal,
    )
      .then((data) => {
        if (!data?.ways) return;
        setWays(data.ways);
        setCenter(data.center);
        setError(null);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Overpass API error:", err);
        setError(err.message || String(err));
      })
      .finally(() => {
        if (inflightRef.current === ctrl) inflightRef.current = null;
      });
    return () => {
      // Don't abort on every grid-cell crossing — that would re-introduce
      // the cancellation storm. The single-flight guard above is enough.
    };
  }, [latKey, lonKey]);

  // Snap on every position update
  useEffect(() => {
    if (!raw || ways.length === 0) { setSnapped(null); return; }
    const s = chooseSnap(raw.lat, raw.lon, ways, {
      heading: raw.heading,
      maxDistance: 35,
      stickyWayId: stickyWayIdRef.current,
      stickThreshold: 8,
    });
    if (s) stickyWayIdRef.current = s.way.id;
    else stickyWayIdRef.current = null;
    setSnapped(s);
  }, [raw, ways]);

  const position: Position | null = !raw
    ? null
    : snapped
    ? { ...raw, lat: snapped.lat, lon: snapped.lon }
    : raw;

  const way = snapped?.way ?? null;
  const limitKmh = parseMaxspeed(way?.tags.maxspeed);
  const limit: SpeedLimit = {
    kmh: limitKmh,
    raw: way?.tags.maxspeed,
    highway: way?.tags.highway ?? null,
    name: way?.tags.name || way?.tags.ref || null,
  };

  return { position, raw, way, limit, error };
}

